import { queryOptions, keepPreviousData } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import {
  PAGE_DEFAULT_LIMIT,
  type Page,
  type StakeholderOrderBy,
  type StakeholderOut,
} from '@/shared/api/types'
import { isValidCnpj, isValidCpf, isValidDocument, onlyDigits } from '@/shared/lib/document'

/**
 * Os 4 "tipos de contato" do nibo (Clientes, Fornecedores, Funcionários, Sócios)
 * são UMA entidade no backend, com discriminador `kind`. Uma tela serve as 4.
 */
export const STAKEHOLDER_KINDS = ['customer', 'supplier', 'employee', 'partner'] as const
export type StakeholderKind = (typeof STAKEHOLDER_KINDS)[number]

/**
 * Tipos de contato oferecidos por DIREÇÃO do lançamento (o que o StakeholderPicker
 * agrupa e o "Criar novo" permite). LIVRE nos dois sentidos — TODOS os tipos: é
 * possível receber de um sócio/funcionário e pagar a um cliente, então o back-end
 * nunca amarrou o kind do contato à direção; a UI acompanha. (Mantido como mapa
 * por direção para poder reintroduzir um recorte no futuro sem mexer nos chamadores.)
 */
export const CONTACT_KINDS_BY_DIRECTION = {
  payable: STAKEHOLDER_KINDS,
  receivable: STAKEHOLDER_KINDS,
} as const satisfies Record<'payable' | 'receivable', readonly StakeholderKind[]>

/** Slug da URL (pt-BR, superfície de produto) ↔ kind da API. */
export const SLUG_TO_KIND = {
  clientes: 'customer',
  fornecedores: 'supplier',
  funcionarios: 'employee',
  socios: 'partner',
} as const satisfies Record<string, StakeholderKind>

export type StakeholderSlug = keyof typeof SLUG_TO_KIND

export const stakeholderKeys = {
  all: () => ['stakeholders'] as const,
  lists: () => [...stakeholderKeys.all(), 'list'] as const,
  list: (p: StakeholderListParams) => [...stakeholderKeys.lists(), p] as const,
} as const

export interface StakeholderListParams {
  readonly kind?: StakeholderKind
  /** Casa por substring, sem diferenciar caixa, no NOME. */
  readonly search?: string
  /** Documento (CPF/CNPJ) — o backend compara só os dígitos. */
  readonly document?: string
  readonly order_by?: StakeholderOrderBy
  readonly limit?: number
  readonly offset?: number
}

export const stakeholdersQueryOptions = (p: StakeholderListParams) =>
  queryOptions({
    queryKey: stakeholderKeys.list(p),
    queryFn: ({ signal }) =>
      api.get<Page<StakeholderOut>>(
        '/v1/stakeholders',
        { ...p, limit: p.limit ?? PAGE_DEFAULT_LIMIT },
        signal,
      ),
    placeholderData: keepPreviousData,
  })

/**
 * Payload de criação — espelha StakeholderCreate do backend.
 *
 * As validações replicam as regras do servidor para dar feedback antes do
 * round-trip. O 422 continua sendo tratado (mapeado para o campo): o servidor é
 * a autoridade, isto é UX.
 */
export const stakeholderFormSchema = z
  .object({
    kind: z.enum(STAKEHOLDER_KINDS),
    name: z.string().trim().min(1, 'validation:required').max(255, 'validation:maxLength'),
    // A validação do documento é CONDICIONAL ao person_type (ver superRefine
    // abaixo): PJ→CNPJ, PF→CPF, Exterior→texto livre, legado→CPF-ou-CNPJ.
    document: z.string().trim().optional(),
    email: z
      .string()
      .trim()
      .refine((v) => v === '' || z.email().safeParse(v).success, 'validation:invalidEmail')
      .optional(),
    // O backend EXIGE DDD: normalize_phone aceita só 10-11 dígitos (fixo ou
    // celular). "99999-8888" (sem DDD) é rejeitado com 422 stakeholder_phone_invalid.
    // Aceita máscara — conta só os dígitos, como o backend faz.
    phone: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d{10,11}$/.test(onlyDigits(v)), 'validation:invalidPhone')
      .optional(),
    // Endereço (colunas nullable no backend — migration 0015). O CEP espelha a
    // regra do servidor (normalize_cep: 8 dígitos, aceita máscara) para dar
    // feedback antes do 422 `stakeholder_zip_code_invalid`.
    zipCode: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d{8}$/.test(onlyDigits(v)), 'validation:invalidZipCode')
      .optional(),
    street: z.string().trim().max(255, 'validation:maxLength').optional(),
    number: z.string().trim().max(20, 'validation:maxLength').optional(),
    complement: z.string().trim().max(255, 'validation:maxLength').optional(),
    neighborhood: z.string().trim().max(255, 'validation:maxLength').optional(),
    city: z.string().trim().max(255, 'validation:maxLength').optional(),
    // UF de 2 letras; o backend faz `.upper()`. Aqui só limitamos o tamanho.
    state: z.string().trim().max(2, 'validation:maxLength').optional(),
    country: z.string().trim().max(60, 'validation:maxLength').optional(),
    // Dados bancários (migration 0016). ESCRITA por `bankId` (id do catálogo
    // /v1/banks); LEITURA vem como `bank` aninhado no StakeholderOut. `''` = sem
    // banco. O tipo de conta reusa o enum de bank_account (checking/…); o backend
    // rejeita valor fora do conjunto com `stakeholder_bank_account_type_invalid`.
    bankId: z.string().optional(),
    bankAgency: z.string().trim().max(20, 'validation:maxLength').optional(),
    bankAccountNumber: z.string().trim().max(30, 'validation:maxLength').optional(),
    bankAccountType: z.string().optional(),
    // Tipo de pessoa (migration 0017): legal=PJ, natural=PF, foreign=Exterior.
    // Opcional: contatos legados vêm sem — a validação de documento cai no
    // CPF-ou-CNPJ de antes quando ausente.
    personType: z.enum(['legal', 'natural', 'foreign']).optional(),
    legalName: z.string().trim().max(255, 'validation:maxLength').optional(),
    municipalRegistration: z.string().trim().max(30, 'validation:maxLength').optional(),
    mobile: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d{10,11}$/.test(onlyDigits(v)), 'validation:invalidPhone')
      .optional(),
    contactPerson: z.string().trim().max(255, 'validation:maxLength').optional(),
    website: z
      .string()
      .trim()
      .refine((v) => v === '' || /^https?:\/\//i.test(v), 'validation:invalidUrl')
      .optional(),
  })
  .superRefine((v, ctx) => {
    // Documento por tipo. Vazio ou Exterior: nada a validar. Ausência de
    // person_type (legado) mantém a regra antiga (CPF-ou-CNPJ).
    if (v.document === undefined || v.document === '' || v.personType === 'foreign') return
    const digitos = onlyDigits(v.document)
    const ok =
      v.personType === 'legal'
        ? isValidCnpj(digitos)
        : v.personType === 'natural'
          ? isValidCpf(digitos)
          : isValidDocument(digitos)
    if (!ok) {
      ctx.addIssue({
        code: 'custom',
        path: ['document'],
        message:
          v.personType === 'legal'
            ? 'validation:invalidCnpj'
            : v.personType === 'natural'
              ? 'validation:invalidCpf'
              : 'validation:invalidDocument',
      })
    }
  })

export type StakeholderFormValues = z.infer<typeof stakeholderFormSchema>

/**
 * Corpo de `POST /v1/stakeholders` — tipado, não Record<string, unknown>.
 * Os opcionais usam `?:` (ausentes), não `| undefined`: o backend distingue
 * "campo não enviado" de "campo vazio".
 */
export interface StakeholderCreateBody {
  readonly kind: StakeholderKind
  readonly name: string
  readonly document?: string
  readonly email?: string
  readonly phone?: string
  readonly zip_code?: string
  readonly street?: string
  readonly number?: string
  readonly complement?: string
  readonly neighborhood?: string
  readonly city?: string
  readonly state?: string
  readonly country?: string
  readonly bank_id?: string
  readonly bank_agency?: string
  readonly bank_account_number?: string
  readonly bank_account_type?: string
  readonly person_type?: string
  readonly legal_name?: string
  readonly municipal_registration?: string
  readonly mobile?: string
  readonly contact_person?: string
  readonly website?: string
}

/** Tipo de pessoa + PJ + contato → corpo (omitir/limpar como os demais). */
function personCreateFields(v: StakeholderFormValues): Partial<StakeholderCreateBody> {
  const presente = (s: string | undefined): s is string => s !== undefined && s !== ''
  return {
    ...(v.personType !== undefined && { person_type: v.personType }),
    ...(presente(v.legalName) && { legal_name: v.legalName }),
    ...(presente(v.municipalRegistration) && {
      municipal_registration: v.municipalRegistration,
    }),
    ...(presente(v.mobile) && { mobile: onlyDigits(v.mobile) }),
    ...(presente(v.contactPerson) && { contact_person: v.contactPerson }),
    ...(presente(v.website) && { website: v.website }),
  }
}

function personUpdateFields(v: StakeholderFormValues) {
  const limpo = (s: string | undefined): string | null => (s !== undefined && s !== '' ? s : null)
  return {
    // person_type ausente = legado → null (mantém a semântica do backend).
    person_type: v.personType ?? null,
    legal_name: limpo(v.legalName),
    municipal_registration: limpo(v.municipalRegistration),
    mobile: v.mobile !== undefined && v.mobile !== '' ? onlyDigits(v.mobile) : null,
    contact_person: limpo(v.contactPerson),
    website: limpo(v.website),
  }
}

/**
 * Endereço → corpo, nas duas semânticas: no CREATE campo vazio é OMITIDO
 * (`undefined`); no PATCH vazio vira `null` (LIMPA). O CEP vai só com dígitos
 * (a máscara é exibição), igual ao `document`.
 */
function addressCreateFields(v: StakeholderFormValues): Partial<StakeholderCreateBody> {
  const zip = v.zipCode !== undefined && v.zipCode !== '' ? onlyDigits(v.zipCode) : undefined
  const presente = (s: string | undefined): s is string => s !== undefined && s !== ''
  return {
    ...(zip !== undefined && { zip_code: zip }),
    ...(presente(v.street) && { street: v.street }),
    ...(presente(v.number) && { number: v.number }),
    ...(presente(v.complement) && { complement: v.complement }),
    ...(presente(v.neighborhood) && { neighborhood: v.neighborhood }),
    ...(presente(v.city) && { city: v.city }),
    ...(presente(v.state) && { state: v.state }),
    ...(presente(v.country) && { country: v.country }),
  }
}

function addressUpdateFields(v: StakeholderFormValues) {
  const limpo = (s: string | undefined): string | null => (s !== undefined && s !== '' ? s : null)
  return {
    zip_code: v.zipCode !== undefined && v.zipCode !== '' ? onlyDigits(v.zipCode) : null,
    street: limpo(v.street),
    number: limpo(v.number),
    complement: limpo(v.complement),
    neighborhood: limpo(v.neighborhood),
    city: limpo(v.city),
    state: limpo(v.state),
    country: limpo(v.country),
  }
}

/** Dados bancários → corpo (mesma semântica omitir/limpar do endereço). */
function bankCreateFields(v: StakeholderFormValues): Partial<StakeholderCreateBody> {
  const presente = (s: string | undefined): s is string => s !== undefined && s !== ''
  return {
    ...(presente(v.bankId) && { bank_id: v.bankId }),
    ...(presente(v.bankAgency) && { bank_agency: v.bankAgency }),
    ...(presente(v.bankAccountNumber) && { bank_account_number: v.bankAccountNumber }),
    ...(presente(v.bankAccountType) && { bank_account_type: v.bankAccountType }),
  }
}

function bankUpdateFields(v: StakeholderFormValues) {
  const limpo = (s: string | undefined): string | null => (s !== undefined && s !== '' ? s : null)
  return {
    bank_id: limpo(v.bankId),
    bank_agency: limpo(v.bankAgency),
    bank_account_number: limpo(v.bankAccountNumber),
    bank_account_type: limpo(v.bankAccountType),
  }
}

/**
 * Form → corpo da API.
 *
 * Campo vazio vira `undefined`, não `''`: o backend valida `document` quando
 * presente, e uma string vazia seria um documento inválido em vez de "não
 * informado". `document` vai só com dígitos — a máscara é exibição.
 */
export function toStakeholderCreateBody(v: StakeholderFormValues): StakeholderCreateBody {
  const doc = v.document !== undefined && v.document !== '' ? onlyDigits(v.document) : undefined
  return {
    kind: v.kind,
    name: v.name,
    ...(doc !== undefined && { document: doc }),
    ...(v.email !== undefined && v.email !== '' && { email: v.email }),
    ...(v.phone !== undefined && v.phone !== '' && { phone: v.phone }),
    ...addressCreateFields(v),
    ...bankCreateFields(v),
    ...personCreateFields(v),
  }
}

/** `POST /v1/stakeholders` — cadastro, NÃO é efeito financeiro: sem Idempotency-Key. */
export function createStakeholder(v: StakeholderFormValues): Promise<StakeholderOut> {
  return api
    .post<StakeholderOut>('/v1/stakeholders', { body: toStakeholderCreateBody(v) })
    .then((r) => r.data)
}

/**
 * Corpo de `PATCH /v1/stakeholders/{id}` — atualização parcial.
 *
 * Diferença semântica do CREATE: aqui campo vazio vira `null` (LIMPA o valor),
 * não `undefined` (omite). O backend distingue os dois — `document/email/phone:
 * null` apagam o campo; omitir mantém. Como o form de edição carrega o estado
 * atual, enviar o que está na tela (com null para "esvaziado") é a intenção.
 * `name`/`kind` são NOT NULL: sempre presentes, nunca null.
 */
export interface StakeholderUpdateBody {
  readonly kind: StakeholderKind
  readonly name: string
  readonly document: string | null
  readonly email: string | null
  readonly phone: string | null
  readonly zip_code: string | null
  readonly street: string | null
  readonly number: string | null
  readonly complement: string | null
  readonly neighborhood: string | null
  readonly city: string | null
  readonly state: string | null
  readonly country: string | null
  readonly bank_id: string | null
  readonly bank_agency: string | null
  readonly bank_account_number: string | null
  readonly bank_account_type: string | null
  readonly person_type: string | null
  readonly legal_name: string | null
  readonly municipal_registration: string | null
  readonly mobile: string | null
  readonly contact_person: string | null
  readonly website: string | null
}

export function toStakeholderUpdateBody(v: StakeholderFormValues): StakeholderUpdateBody {
  const doc = v.document !== undefined && v.document !== '' ? onlyDigits(v.document) : null
  return {
    kind: v.kind,
    name: v.name,
    document: doc,
    email: v.email !== undefined && v.email !== '' ? v.email : null,
    phone: v.phone !== undefined && v.phone !== '' ? v.phone : null,
    ...addressUpdateFields(v),
    ...bankUpdateFields(v),
    ...personUpdateFields(v),
  }
}

/** `PATCH /v1/stakeholders/{id}` — edição de cadastro; sem Idempotency-Key. */
export function updateStakeholder(id: string, v: StakeholderFormValues): Promise<StakeholderOut> {
  return api.patch<StakeholderOut>(`/v1/stakeholders/${id}`, toStakeholderUpdateBody(v))
}

/**
 * `DELETE /v1/stakeholders/{id}` — SOFT delete.
 * 409 `stakeholder_has_schedules` se o contato tem agendamentos: a tela orienta.
 */
export function deleteStakeholder(id: string): Promise<void> {
  return api.del(`/v1/stakeholders/${id}`)
}
